import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/catch';
import { concat } from 'rxjs/observable/concat';
import { type ActionsObservable } from 'redux-observable';
import { combineLatest } from 'rxjs/observable/combineLatest';
import {
	UPDATE_ONDEMAND_ACTIVATION_MODAL_STATE,
	updateOnDemandActivationModalState,
} from '../ui/ondemand-activation/actions';
import { getActivationStatus, getUserInitiatedActivation } from '../activation/selectors';
import { initializeView } from '../actions/initialize-view';
import { updateConfluenceState } from '../actions';
import {
	MARK_ACTIVATION_COMPLETED,
	type markActivationCompleted,
	updateConfluenceActivationStatus,
} from '../actions/activate-confluence';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';

import { type Store } from 'redux';
import { type State } from '../types';

type Cohort = 'not-enrolled' | 'control' | 'variation';

type Action =
	| ReturnType<typeof markActivationCompleted>
	| ReturnType<typeof updateOnDemandActivationModalState>;

function isExperimentEnabled() {
	const cohort = FeatureGates.getExperimentValue<Cohort>(
		'project_pages_embedded_confluence_experiment',
		'cohort',
		'not-enrolled',
		// NOTE: This is on purpose, we do not want to overexpose at this point given that users haven't actually started on-demand activation yet.
		{ fireExperimentExposure: false },
	);

	return fg('project_pages_embededed_confluence_gate') && cohort === 'variation';
}

function getObservables(action$: ActionsObservable<Action>) {
	if (isExperimentEnabled()) {
		return combineLatest(action$.ofType(MARK_ACTIVATION_COMPLETED));
	}
	return combineLatest(
		action$.ofType(UPDATE_ONDEMAND_ACTIVATION_MODAL_STATE),
		action$.ofType(MARK_ACTIVATION_COMPLETED),
	);
}

export default (action$: ActionsObservable<Action>, store: Store<State>) => {
	return getObservables(action$)
		.filter(([modalStateAction]: Action[]) => {
			const isActivating = getActivationStatus(store.getState()) === 'activating';
			if (isExperimentEnabled()) {
				return isActivating;
			}
			return modalStateAction.payload?.modalState === 'closed' && isActivating;
		})
		.switchMap(() => {
			const userInitiated = getUserInitiatedActivation(store.getState());
			if (userInitiated && isExperimentEnabled()) {
				return concat([
					// Only close modal if user is in variation
					updateOnDemandActivationModalState({
						modalState: 'closed',
					}),
					updateConfluenceState('CONFLUENCE_LOADING'),
					updateConfluenceActivationStatus({
						status: 'completed',
					}),
					initializeView(),
				]);
			}
			return concat([
				updateConfluenceState('CONFLUENCE_LOADING'),
				updateConfluenceActivationStatus({
					status: 'completed',
				}),
				initializeView(),
			]);
		});
};
